import { useEffect, useState } from 'react';
import { FileCsv, FilePdf, FileText, FileXls, Image as ImageIcon, Paperclip, X } from '@phosphor-icons/react';
import { formatBytes } from '../lib/format';

/**
 * One attached file, drawn from what is actually known about it rather than a
 * generic paperclip: the real type, an image thumbnail when the browser can
 * make one, the size in Persian digits, and — for anything the importer cannot
 * read — a plain sentence saying so, before the upload rather than after it
 * fails.
 */

const IMAGE_EXTENSIONS: ReadonlySet<string> = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'avif', 'svg']);
const SEPARATED_EXTENSIONS: ReadonlySet<string> = new Set(['csv', 'tsv']);
const SPREADSHEET_EXTENSIONS: ReadonlySet<string> = new Set(['xls', 'xlsx', 'xlsm', 'xlsb', 'ods']);

export function fileExtension(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? (parts.pop() ?? '').toLocaleLowerCase('en-US') : '';
}

function TypeIcon({ extension, mimeType }: { extension: string; mimeType: string }) {
  if (IMAGE_EXTENSIONS.has(extension) || mimeType.startsWith('image/')) return <ImageIcon size={17} />;
  if (SEPARATED_EXTENSIONS.has(extension)) return <FileCsv size={17} />;
  if (SPREADSHEET_EXTENSIONS.has(extension)) return <FileXls size={17} />;
  if (extension === 'pdf') return <FilePdf size={17} />;
  if (['txt', 'md', 'doc', 'docx', 'rtf'].includes(extension)) return <FileText size={17} />;
  return <Paperclip size={17} />;
}

export function AttachmentRow({
  name,
  sizeBytes,
  mimeType = '',
  file = null,
  error = null,
  readable = true,
  onRemove,
}: {
  name: string;
  sizeBytes: number;
  mimeType?: string;
  /** The picked file while it is still local — used only to draw a thumbnail. */
  file?: File | null;
  /** A problem with this one file, in Persian. */
  error?: string | null;
  /**
   * Whether the importer can open this format. Passed in rather than decided
   * here, because the authority on that is the server's own `uploadFormats`.
   */
  readable?: boolean;
  onRemove?: () => void;
}) {
  const extension = fileExtension(name);
  const isImage = IMAGE_EXTENSIONS.has(extension) || mimeType.startsWith('image/');
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  // The object URL lives and dies with this row, so it cannot outlive the
  // thumbnail that used it — React runs the cleanup both when the row is
  // removed and when the whole composer unmounts.
  useEffect(() => {
    if (!file || !isImage || typeof URL?.createObjectURL !== 'function') return undefined;
    const objectUrl = URL.createObjectURL(file);
    setThumbnail(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
      setThumbnail(null);
    };
  }, [file, isImage]);

  return (
    <li className={`attachment-row${readable ? '' : ' attachment-row--unreadable'}${error ? ' attachment-row--error' : ''}`}>
      <span className="attachment-row__figure">
        {thumbnail
          ? <img className="attachment-row__thumb" src={thumbnail} alt="" />
          : <TypeIcon extension={extension} mimeType={mimeType} />}
      </span>

      <span className="attachment-row__copy">
        {/* `bdi` so a latin file name cannot drag the row's punctuation to the
            wrong end of an RTL line. */}
        <b className="attachment-row__name"><bdi dir="auto">{name}</bdi></b>
        <small className="attachment-row__meta">
          {formatBytes(sizeBytes)}
          {extension && <> · <bdi dir="ltr">{extension.toLocaleUpperCase('en-US')}</bdi></>}
        </small>
        {!readable && !error && <small className="attachment-row__note">این قالب خوانده نمی‌شود</small>}
        {error && <small className="attachment-row__error" role="alert">{error}</small>}
      </span>

      {onRemove && (
        <button
          type="button"
          className="attachment-row__remove"
          aria-label={`حذف ${name}`}
          title="حذف"
          onClick={onRemove}
        >
          <X size={13} weight="bold" />
        </button>
      )}
    </li>
  );
}
